import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { Form, Formik } from "formik";
import {
  DragDropProvider,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
} from "@dnd-kit/react";
import type { DragEndEvent } from "@dnd-kit/dom";

import { ColumnDnd } from "../Alert/AlertDragDrop";
import { kpiIdOrder } from "../Alert/kpiIds";
import { greyOutTemporaryKpis } from "../Alert/Filter/CreateFilterKpiForm";
import { KpiWithGroupId } from "../Alert/Filter/FilterType";
import { Column, Row } from "../Layout/utils";
import { DragDrop } from "./DragDrop";
import { SCROLL_BAR_STYLES_THIN } from "../Theme";
import { FormattedMessage, useIntl } from "react-intl";
import { ActionTooltip } from "./Tooltips";

export type ColumnGroupedDnd = {
  name: string;
  items: GroupedKpis[];
};

type Props = {
  firstColumn: ColumnGroupedDnd;
  secondColumn: ColumnDnd;
  onSubmit: (listKpisId: kpiIdOrder[]) => void;
};

export type GroupedKpis = {
  id: number;
  label: string;
  kpiList: KpiWithGroupId[];
};

type DragData = {
  column: "source" | "target";
  itemId?: number;
  groupId?: number;
  index?: number;
};

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    backgroundColor: theme.vars.palette.background.default,
    overflowY: "auto",
    maxHeight: "300px",
    ...SCROLL_BAR_STYLES_THIN,
  },
  addIcon: {
    padding: "3px",
  },
}));

export const DragDropPerTypeContainer = ({
  firstColumn,
  secondColumn,
  onSubmit,
}: Props) => {
  const intl = useIntl();
  const [leftColumn, setLeftColumn] = useState<ColumnGroupedDnd>(firstColumn);
  const [rightColumn, setRightColumn] = useState<ColumnDnd>(secondColumn);

  const moveKpi = useCallback(
    (itemId: number, action: string, groupId?: number, index?: number) => {
      if (action === "ajouter") {
        const group = leftColumn.items.find((item) => item.id === groupId);
        if (group) {
          const itemToAddIndex = group.kpiList.findIndex(
            (item) => item.id === itemId
          );
          const itemToAdd = group.kpiList.find((item) => item.id === itemId);
          group.kpiList.splice(itemToAddIndex, 1);
          if (itemToAdd) {
            index !== undefined
              ? rightColumn.items.splice(index, 0, itemToAdd)
              : rightColumn.items.push(itemToAdd);
          }
        }
      } else {
        const itemToRemoveIndex = rightColumn.items.findIndex(
          (item) => item.id === itemId
        );
        const itemToRemove = rightColumn.items.find(
          (item) => item.id === itemId
        );
        rightColumn.items.splice(itemToRemoveIndex, 1);

        const group = leftColumn.items.find(
          (item) => item.id === (itemToRemove as KpiWithGroupId).groupId
        );
        if (group && itemToRemove) {
          group.kpiList.push(itemToRemove as KpiWithGroupId);
        }
      }
      setLeftColumn({ ...leftColumn });
      setRightColumn({ ...rightColumn });
    },
    [leftColumn, rightColumn]
  );

  const onDragEnd = useCallback(
    (event: Parameters<DragEndEvent>[0]) => {
      const { operation } = event;
      if (!operation.source || !operation.target) return;

      const src = operation.source.data as DragData;
      const tgt = operation.target.data as DragData;

      if (src.column === "source" && tgt.column === "target") {
        moveKpi(src.itemId!, "ajouter", src.groupId, tgt.index);
      } else if (src.column === "target" && tgt.column === "source") {
        moveKpi(src.itemId!, "supprimer");
      } else if (
        src.column === "target" &&
        tgt.column === "target" &&
        src.index !== tgt.index
      ) {
        const items = [...rightColumn.items];
        const insertAt = src.index! < tgt.index! ? tgt.index! - 1 : tgt.index!;
        const [removed] = items.splice(src.index!, 1);
        if (removed) items.splice(insertAt, 0, removed);
        setRightColumn({ name: rightColumn.name, items });
      }
    },
    [moveKpi, rightColumn]
  );

  if (!leftColumn || !rightColumn) return <div> chargement des données</div>;

  return (
    <Formik
      initialValues={[]}
      onSubmit={() =>
        onSubmit(
          rightColumn.items.map((val, index) => ({ order: index, id: val.id }))
        )
      }
    >
      <Form id="create-filter-form">
        <Box sx={{ display: "flex", gap: 1, marginBottom: 1 }}>
          <Box sx={{ width: "50%" }}>
            <Typography>
              <FormattedMessage id="GLOSSARY" />
            </Typography>
            <Typography
              component={"p"}
              sx={{ fontSize: "0.875rem", color: "text.secondary" }}
            >
              {intl.formatMessage(
                { id: "GLOSSARY_DRAG_DROP_TEXT" },
                { br: <br /> }
              )}
            </Typography>
          </Box>
          <Box sx={{ width: "50%" }}>
            <Typography>
              <FormattedMessage id="TEMPORALITY" />
            </Typography>
            <Typography
              component={"p"}
              sx={{ fontSize: "0.875rem", color: "text.secondary" }}
            >
              {intl.formatMessage(
                { id: "TEMPORALITY_DRAG_DROP_TEXT" },
                { br: <br /> }
              )}
            </Typography>
          </Box>
        </Box>
        <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Box
            style={{
              display: "flex",
              flexGrow: 1,
            }}
          >
            <Row
              sx={{
                gap: 1,
                flexGrow: 1,
                width: "100%",
                px: 1,
                backgroundColor: "background.paper",
              }}
            >
              <DragDropProvider
                sensors={[PointerSensor, KeyboardSensor]}
                onDragEnd={onDragEnd}
              >
                <DragDropPerType
                  list={leftColumn.items}
                  title={leftColumn.name}
                  columnId={"source"}
                  icon={<AddIcon color="primary" fontSize="small" />}
                  buttonClick={(itemId, groupId) =>
                    moveKpi(itemId, "ajouter", groupId)
                  }
                />
                <DragDrop
                  list={rightColumn.items}
                  title={rightColumn.name}
                  columnId={"target"}
                  icon={
                    <ClearIcon fontSize="small" sx={{ color: "error.main" }} />
                  }
                  buttonClick={(itemId) => moveKpi(itemId, "supprimer")}
                />
              </DragDropProvider>
            </Row>
          </Box>
        </div>
      </Form>
    </Formik>
  );
};

const DroppableSourceGroup = ({
  group,
  children,
}: {
  group: GroupedKpis;
  children: React.ReactNode;
}) => {
  const { ref } = useDroppable({
    id: `droppable-source-${group.id}`,
    data: { column: "source", groupId: group.id } as DragData,
  });
  return (
    <div ref={ref as React.RefCallback<HTMLDivElement>} style={{ flexGrow: 1 }}>
      {children}
    </div>
  );
};

export const DragDropPerType = ({
  list,
  title,
  columnId,
  icon,
  buttonClick,
}: {
  list: GroupedKpis[];
  title: string;
  columnId: string;
  icon: JSX.Element;
  buttonClick: (itemId: number, groupId: number) => void;
}) => {
  const { classes } = useStyles();
  return (
    <Column
      sx={{
        width: "0px",
      }}
    >
      <Row
        sx={{
          alignItems: "center",
          justifyContent: "center",
          height: "48px",
          color: "text.secondary",
        }}
      >
        <Typography component={"span"}>{title}</Typography>
      </Row>
      <div className={classes.root}>
        {list.map((group) => (
          <DroppableSourceGroup key={columnId + group.id} group={group}>
            <DraggableList
              group={group}
              icon={icon}
              buttonClick={buttonClick}
            />
          </DroppableSourceGroup>
        ))}
      </div>
    </Column>
  );
};

const DraggableSourceItem = ({
  item,
  index,
  icon,
  buttonClick,
}: {
  item: KpiWithGroupId;
  index: number;
  icon: JSX.Element;
  buttonClick: (itemId: number, groupId: number) => void;
}) => {
  const { classes } = useStyles();
  const isDragDisabled = greyOutTemporaryKpis.includes(item.id);
  const { ref, isDragging } = useDraggable({
    id: `source-${item.id}-${item.groupId}`,
    data: {
      column: "source",
      itemId: item.id,
      groupId: item.groupId,
      index,
    } as DragData,
    disabled: isDragDisabled,
  });

  return (
    <ListItemButton
      ref={ref as React.RefCallback<HTMLDivElement>}
      disabled={isDragDisabled}
      sx={{
        userSelect: "none",
        opacity: isDragging ? 0.4 : 1,
        backgroundColor: "background.paper",
        padding: "0",
        marginTop: "2px",
        width: "initial",
        cursor: isDragDisabled ? "default" : "grab",
      }}
    >
      <WithTooltip
        title={item.label}
        style={{
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        <ListItemText
          primary={item.label}
          style={{
            margin: "0",
            padding: "0 0 0 16px",
          }}
          slotProps={{
            primary: {
              style: {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            },
          }}
        />
      </WithTooltip>
      <ListItemIcon style={{ minWidth: "initial" }}>
        <IconButton
          className={classes.addIcon}
          onClick={() => buttonClick(item.id, item.groupId)}
          disabled={isDragDisabled}
        >
          {icon}
        </IconButton>
      </ListItemIcon>
    </ListItemButton>
  );
};

export const DraggableList = ({
  group,
  icon,
  buttonClick,
  isFirst,
}: {
  group: GroupedKpis;
  icon: JSX.Element;
  buttonClick: (itemId: number, groupId: number) => void;
  isFirst?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <List
        style={{
          padding: "0",
          margin: `${isFirst ? "5px" : "0"} 5px 5px 5px`,
        }}
      >
        <ListItemButton
          onClick={() => setOpen((prevState: boolean) => !prevState)}
          sx={{
            padding: "0",
            backgroundColor: "background.paper",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            flexGrow: 1,
            width: "initial",
          }}
        >
          <ListItemText
            primary={group.label}
            sx={{
              padding: "0px 0px 0px 8px",
              margin: "2px 0px 0px 0px",
              color: "text.secondary",
            }}
          />
          {open ? (
            <ExpandLessIcon sx={{ color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon sx={{ color: "text.secondary" }} />
          )}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {group.kpiList.map((item, index) => (
              <DraggableSourceItem
                key={item.id}
                item={item}
                index={index}
                icon={icon}
                buttonClick={buttonClick}
              />
            ))}
          </List>
        </Collapse>
      </List>
    </>
  );
};

export const WithTooltip = ({
  title,
  style,
  children,
}: PropsWithChildren<{ title: string; style: any }>) => {
  const [isOverflowed, setIsOverflow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {}, [ref]);

  useEffect(() => {
    if (ref.current) {
      const current =
        ref.current.children[0] && ref.current.children[0].children[0]
          ? ref.current.children[0].children[0]
          : false;

      if (current) setIsOverflow(current.scrollWidth > current.clientWidth);
    }
  }, []);

  return (
    <ActionTooltip
      title={title}
      translateTitle={false}
      rawChildren
      disableHoverListener={!isOverflowed}
    >
      <div ref={ref} style={{ width: "100%", ...style }}>
        {children}
      </div>
    </ActionTooltip>
  );
};
